import type { IconTone } from '@/components/common/GradientIcon';

export const trustedPlatforms = ['BloomCommerce', 'NovaMarket', 'Kartel', 'Loop', 'Vantage', 'Cedar', 'Orbit', 'Lumen'];

// ---- Solutions carousel (tabs) ----
export type SolutionVisual = 'chart' | 'ring' | 'list' | 'chat' | 'stat' | 'tiers' | 'code';

export interface Solution {
  tag: string;
  icon: string;
  tone: IconTone;
  title: string;
  visual: SolutionVisual;
  gradient: string; // Tailwind gradient classes
}

export const solutionTabs: { id: string; label: string; items: Solution[] }[] = [
  {
    id: 'practice',
    label: 'Best practices',
    items: [
      { tag: 'Points engine', icon: 'ri-coins-fill', tone: 'blue', title: 'Earn rules in minutes', visual: 'chart', gradient: 'from-[#dfe8ff] to-[#eef3ff]' },
      { tag: 'Rewards', icon: 'ri-gift-2-fill', tone: 'violet', title: 'Reward catalog', visual: 'list', gradient: 'from-[#e9e2ff] to-[#f4f0ff]' },
      { tag: 'Tiers', icon: 'ri-vip-crown-2-fill', tone: 'indigo', title: 'Member tiers', visual: 'tiers', gradient: 'from-[#e3e6ff] to-[#f1f3ff]' },
      { tag: 'Redemption', icon: 'ri-coupon-3-fill', tone: 'pink', title: 'Instant redemption', visual: 'ring', gradient: 'from-[#ffe3ee] to-[#fff1f6]' },
      { tag: 'Engagement', icon: 'ri-chat-smile-3-fill', tone: 'cyan', title: 'Member messaging', visual: 'chat', gradient: 'from-[#dcf3ff] to-[#eef9ff]' },
      { tag: 'Analytics', icon: 'ri-line-chart-fill', tone: 'teal', title: 'Program analytics', visual: 'stat', gradient: 'from-[#d6f5ee] to-[#ebfbf7]' },
      { tag: 'Developers', icon: 'ri-code-box-fill', tone: 'blue', title: 'One clean API', visual: 'code', gradient: 'from-[#dfe8ff] to-[#f1f5ff]' },
    ],
  },
  {
    id: 'industry',
    label: 'By industry',
    items: [
      { tag: 'Retail', icon: 'ri-store-2-fill', tone: 'orange', title: 'Stores & chains', visual: 'stat', gradient: 'from-[#ffeedd] to-[#fff6ec]' },
      { tag: 'Food & drink', icon: 'ri-cup-fill', tone: 'pink', title: 'Cafés & restaurants', visual: 'ring', gradient: 'from-[#ffe3ee] to-[#fff1f6]' },
      { tag: 'E-commerce', icon: 'ri-shopping-bag-3-fill', tone: 'violet', title: 'Online shops', visual: 'chart', gradient: 'from-[#e9e2ff] to-[#f4f0ff]' },
      { tag: 'Fintech', icon: 'ri-bank-card-fill', tone: 'blue', title: 'Wallets & banks', visual: 'code', gradient: 'from-[#dfe8ff] to-[#eef3ff]' },
      { tag: 'Mobility', icon: 'ri-taxi-fill', tone: 'teal', title: 'Ride & delivery apps', visual: 'list', gradient: 'from-[#d6f5ee] to-[#ebfbf7]' },
    ],
  },
  {
    id: 'role',
    label: 'By role',
    items: [
      { tag: 'Marketing', icon: 'ri-megaphone-fill', tone: 'pink', title: 'Run campaigns', visual: 'chart', gradient: 'from-[#ffe3ee] to-[#fff1f6]' },
      { tag: 'Operations', icon: 'ri-settings-5-fill', tone: 'indigo', title: 'Approve redemptions', visual: 'list', gradient: 'from-[#e3e6ff] to-[#f1f3ff]' },
      { tag: 'Developers', icon: 'ri-terminal-box-fill', tone: 'blue', title: 'Integrate in a day', visual: 'code', gradient: 'from-[#dfe8ff] to-[#f1f5ff]' },
      { tag: 'Finance', icon: 'ri-funds-fill', tone: 'green', title: 'Settle sponsors', visual: 'stat', gradient: 'from-[#dcf7e6] to-[#effcf4]' },
    ],
  },
];

// ---- Highlighted benefits ----
export const benefits = [
  { tag: 'Points engine', icon: 'ri-coins-fill', tone: 'blue' as IconTone, title: 'Flexible earning rules', text: 'Per-action rules, sign-up bonuses and campaigns', art: 'from-[#c9d8ff] to-[#eaf0ff]' },
  { tag: 'Rewards', icon: 'ri-gift-2-fill', tone: 'violet' as IconTone, title: 'A catalog members love', text: 'Vouchers, merch and experiences with stock control', art: 'from-[#e2d9ff] to-[#f5f1ff]' },
  { tag: 'Sponsors', icon: 'ri-hand-heart-fill', tone: 'pink' as IconTone, title: 'Sponsor-funded rewards', text: 'Brands fund rewards and track every redemption', art: 'from-[#ffd9e6] to-[#fff0f5]' },
];

export const promos = [
  { title: 'Join the Loyalty Ready program', text: 'Free onboarding call for new platforms', badge: 'NEW', icon: 'ri-rocket-2-fill', tone: 'violet' as IconTone },
  { title: 'Refer a platform', text: 'Get up to 3 months of Growth free', badge: 'GIFT', icon: 'ri-gift-fill', tone: 'orange' as IconTone },
];

// ---- Product suite (icon grid) ----
export const suite = [
  {
    label: 'Earn',
    title: 'Grow engagement, reward every action',
    wide: true,
    items: [
      { icon: 'ri-coins-fill', tone: 'blue' as IconTone, name: 'Points', text: 'Real-time balances' },
      { icon: 'ri-flashlight-fill', tone: 'orange' as IconTone, name: 'Earning rules', text: 'Per action & spend' },
      { icon: 'ri-vip-crown-2-fill', tone: 'indigo' as IconTone, name: 'Tiers', text: 'Auto level-up' },
      { icon: 'ri-megaphone-fill', tone: 'pink' as IconTone, name: 'Campaigns', text: 'Bonus periods' },
      { icon: 'ri-group-fill', tone: 'teal' as IconTone, name: 'Members', text: 'Profiles & history' },
      { icon: 'ri-user-shared-fill', tone: 'violet' as IconTone, name: 'Referrals', text: 'Invite rewards' },
      { icon: 'ri-notification-3-fill', tone: 'cyan' as IconTone, name: 'Notifications', text: 'Email & push' },
    ],
  },
  {
    label: 'Redeem',
    title: 'Rewards members actually use',
    items: [
      { icon: 'ri-gift-2-fill', tone: 'violet' as IconTone, name: 'Catalog', text: 'Stock & categories' },
      { icon: 'ri-coupon-3-fill', tone: 'pink' as IconTone, name: 'Vouchers', text: 'Codes & QR' },
      { icon: 'ri-checkbox-multiple-fill', tone: 'green' as IconTone, name: 'Approvals', text: 'Manual or auto' },
    ],
  },
  {
    label: 'Build',
    title: 'Developer tools, ready for any platform',
    items: [
      { icon: 'ri-key-2-fill', tone: 'orange' as IconTone, name: 'API keys', text: 'Scoped & hashed' },
      { icon: 'ri-webhook-fill', tone: 'blue' as IconTone, name: 'Webhooks', text: 'Event delivery' },
      { icon: 'ri-file-list-3-fill', tone: 'teal' as IconTone, name: 'Ledger', text: 'Full audit trail' },
    ],
  },
];

// ---- Customer stories ----
export const stories = [
  {
    company: 'BloomCommerce',
    name: 'Maya Chen',
    role: 'Head of Product',
    metric: '3.2x',
    metricLabel: 'repeat purchases in 6 months',
    quote: 'We went from zero loyalty program to full points and redemption in a weekend. The ledger saved our support team hours.',
  },
  {
    company: 'NovaMarket',
    name: 'Daniel Okafor',
    role: 'Founder',
    metric: '41%',
    metricLabel: 'more active members',
    quote: 'Customers have a reason to come back, without us building any loyalty infrastructure.',
  },
  {
    company: 'Kartel',
    name: 'Sofia Rossi',
    role: 'Growth Lead',
    metric: '< 1 day',
    metricLabel: 'to onboard a partner',
    quote: 'Tiers and auto-fulfilment are exactly what we needed.',
  },
];

// ---- Services ----
export const services = [
  { icon: 'ri-compass-3-fill', tone: 'blue' as IconTone, title: 'Program design', text: 'Plan earning rules and tiers with our team' },
  { icon: 'ri-customer-service-2-fill', tone: 'violet' as IconTone, title: '24/7 support', text: 'Priority email and chat on paid plans' },
  { icon: 'ri-tools-fill', tone: 'teal' as IconTone, title: 'Technical support', text: 'Integration help, one-to-one' },
  { icon: 'ri-lightbulb-flash-fill', tone: 'orange' as IconTone, title: 'Consulting', text: 'Campaign and reward strategy' },
  { icon: 'ri-question-answer-fill', tone: 'green' as IconTone, title: 'Help center', text: 'Guides, FAQs and video walkthroughs' },
  { icon: 'ri-swap-box-fill', tone: 'pink' as IconTone, title: 'Migration', text: 'Move members and balances from your old system' },
];

// ---- Security ----
export const securityCards = [
  { icon: 'ri-shield-check-fill', tone: 'blue' as IconTone, kicker: 'End-to-end protection', title: 'Keeps member data safe' },
  { icon: 'ri-global-fill', tone: 'indigo' as IconTone, kicker: 'Built for any market', title: 'One platform, every region' },
];

export const securityControls = [
  { icon: 'ri-key-2-fill', label: 'Hashed API keys' },
  { icon: 'ri-lock-2-fill', label: 'TLS everywhere' },
  { icon: 'ri-shield-user-fill', label: 'Scoped access' },
  { icon: 'ri-file-shield-2-fill', label: 'Audit log' },
  { icon: 'ri-database-2-fill', label: 'Daily backups' },
  { icon: 'ri-fingerprint-fill', label: 'OTP sign-in' },
  { icon: 'ri-eye-off-fill', label: 'Secrets never returned' },
  { icon: 'ri-speed-up-fill', label: 'Rate limiting' },
];

// ---- Resources ----
export const resources = [
  { tag: 'Quickstart', title: 'Connect your platform in 10 minutes', meta: '5 lessons', icon: 'ri-rocket-2-fill', tone: 'blue' as IconTone, art: 'from-[#dfe8ff] to-[#f4f7ff]' },
  { tag: 'Guide', title: 'Design earning rules that drive repeat visits', meta: '8 lessons', icon: 'ri-coins-fill', tone: 'orange' as IconTone, art: 'from-[#ffeedd] to-[#fff8f0]' },
  { tag: 'API', title: 'Earn, redeem and balance endpoints explained', meta: '6 lessons', icon: 'ri-code-box-fill', tone: 'violet' as IconTone, art: 'from-[#e9e2ff] to-[#f7f4ff]' },
  { tag: 'Webinar', title: 'Running sponsor-funded reward campaigns', meta: '1 session', icon: 'ri-live-fill', tone: 'pink' as IconTone, art: 'from-[#ffe3ee] to-[#fff4f8]' },
];

export const faqs = [
  {
    q: 'How do I connect my platform to LoyaltyBridge?',
    a: 'Create an integration in your dashboard to generate a scoped API key. Then call the member endpoint to register users and the earn endpoint to award points. Full reference is in our developer docs.',
  },
  {
    q: 'What does it cost?',
    a: 'LoyaltyBridge has a free tier for small programs, with paid plans that scale by members and API volume. There are no per-redemption fees.',
  },
  {
    q: 'Can I customize earning rules and tiers?',
    a: 'Yes. Set points-per-currency, sign-up bonuses and tier thresholds in program settings. Earning rules apply across every connected platform.',
  },
  {
    q: 'Is my member data secure?',
    a: 'API keys are hashed and scoped, every change is recorded in an audit log, and redemption approvals can require manual review if you choose.',
  },
  {
    q: 'Can members redeem rewards on their own?',
    a: 'Yes. Expose your reward catalog to members and they redeem directly. You approve or auto-fulfil depending on your policy.',
  },
];
